/* Global toast system — replaces alert() and silent .catch(() => {}).
 *
 * Usage:
 *   import { toast, ToastHost } from "../components/Toast";
 *   toast.error("Something went wrong");
 *   toast.success("Saved");
 *
 * Mount <ToastHost /> once at the app root. (audit U9/U13)
 */
import { useEffect, useState } from "react";

let _listeners = new Set();
let _seq = 0;

function _emit(t) {
  const id = ++_seq;
  const entry = { id, ...t };
  _listeners.forEach((fn) => fn(entry));
  return id;
}

export const toast = {
  info: (message, opts = {}) => _emit({ kind: "info", message, ttl: 4000, ...opts }),
  success: (message, opts = {}) => _emit({ kind: "success", message, ttl: 3000, ...opts }),
  warning: (message, opts = {}) => _emit({ kind: "warning", message, ttl: 5000, ...opts }),
  error: (message, opts = {}) => _emit({ kind: "error", message, ttl: 7000, ...opts }),
};

const KIND_CLASSES = {
  info: "bg-blue-600",
  success: "bg-emerald-600",
  warning: "bg-amber-600",
  error: "bg-red-600",
};

export function ToastHost() {
  const [items, setItems] = useState([]);

  useEffect(() => {
    const push = (entry) => {
      setItems((prev) => [...prev, entry]);
      if (entry.ttl > 0) {
        setTimeout(() => setItems((prev) => prev.filter((x) => x.id !== entry.id)), entry.ttl);
      }
    };
    _listeners.add(push);
    return () => { _listeners.delete(push); };
  }, []);

  const dismiss = (id) => setItems((prev) => prev.filter((x) => x.id !== id));

  return (
    <div
      role="status"
      aria-live="polite"
      className="fixed bottom-4 right-4 z-[100] flex flex-col gap-2 max-w-sm"
    >
      {items.map((t) => (
        <div
          key={t.id}
          className={`text-white px-4 py-2 rounded-lg shadow-lg flex items-start gap-2 ${KIND_CLASSES[t.kind] || KIND_CLASSES.info}`}
          role={t.kind === "error" ? "alert" : "status"}
        >
          <div className="text-sm flex-1 whitespace-pre-line break-words">{t.message}</div>
          <button
            onClick={() => dismiss(t.id)}
            className="text-white/80 hover:text-white text-lg leading-none"
            aria-label="Dismiss notification"
          >×</button>
        </div>
      ))}
    </div>
  );
}

/**
 * Wrap a .catch() so a non-fatal failure surfaces as a toast instead
 * of a silent empty state. Preserves the original .then() flow for
 * happy paths by returning undefined on error.
 *
 * Usage:
 *   api.get("/foo").then(setFoo).catch(reportError("Couldn't load foo"));
 */
export function reportError(prefix = "Something went wrong") {
  return (err) => {
    const msg = err?.message || String(err);
    toast.error(`${prefix}: ${msg}`);
  };
}
